import moment from 'moment';
import { useEffect } from 'react';
import React from 'react';
import { Button, Form, Header, Icon, Label, Loader, Popup, Select } from 'semantic-ui-react';
import type { DropdownProps } from 'semantic-ui-react';
import type { Option } from 'types/option';
import { softDeleteObservation } from 'utils/softDeleteObservation';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRPoster from '../../../data/FHIRPoster';
import { ObservationBuilder } from '../../../fhir-sdoh';
import { parseFHIRPreference } from '../../../utils/FHIRUtils';
import { getHighlightedObservationIds } from '../../../utils/observationHighlightUtil';

import type { ProfileChildBaseProps, ProfileMutate, ProfileObservationEntry, RejectionWithMessage, SubmittedForm } from './types';

/**
 * `patientId` and `mutate` are required: both flow unguarded into
 * `ObservationBuilder`/`softDeleteObservation` and `props.mutate().then(...)`.
 * See the defect note in `vitals.tsx` — `patientId` originates as FHIR's `0..1`
 * `Patient.id`.
 */
type ObservationsProps = Omit<ProfileChildBaseProps, 'patientId'> & {
  /** The member's FHIR `Patient.id`. */
  patientId: string;
  /**
   * The comma-separated observation codes this panel renders, forwarded from
   * one `profileCodes` entry in `./index.tsx`. Compared against several literal
   * codes to decide which add-control (if any) to offer, and stamped onto
   * Observations created here.
   */
  code: string;
  /** Observation entries for this code, from `Profile.getDataByCode`. */
  data?: ProfileObservationEntry[];
  /** Revalidates the profile query after an observation is added or removed. */
  mutate: ProfileMutate;
};

export default function Observations(props: ObservationsProps) {
  const [loading, setLoading] = React.useState(!props.data ? 'Loading Profile Data...' : '');
  const [popOpen, setpopOpen] = React.useState(false);
  // These three hold the selected `<Select>` value, which semantic-ui hands back
  // as `DropdownProps['value']` — wider than the `string` they are seeded with
  // and than `submitSuggestion` ultimately wants. See the note there.
  const [resourceOption, setResourceOption] = React.useState<DropdownProps['value']>('');
  const [communicationOption, setCommunicationOption] = React.useState<DropdownProps['value']>('');
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  /**
   * Spelling suggestions for the typed value. Always empty in this build — the
   * suggestion service needs a credential that cannot be held in a browser
   * bundle (see `handleSubmit`), so only the `[0]` entry, the value the user
   * typed, is ever present.
   */
  const [suggestions, setSuggestions] = React.useState<string[]>([]);
  const [educationFilter, setEducationFilter] = React.useState<string | null>(null);
  const [eligibilityStatusOption, setEligibilityStatusOption] =
    React.useState<DropdownProps['value']>('');

  const handlePopOpen = () => {
    setpopOpen(true);
    setShowSuggestions(false);
    setSuggestions([]);
  };
  const handleClose = () => {
    setpopOpen(false);
    setShowSuggestions(false);
    setSuggestions([]);
    setResourceOption('');
    setCommunicationOption('');
    setEligibilityStatusOption('');
  };

  const [highlightID, setHighlightID] = React.useState<string[]>([]);

  // SUSPECTED DEFECT — `props.data` is optional (it is absent until the parent's
  // profile query resolves) but `getHighlightedObservationIds` indexes its
  // argument as `data[0]` with no guard, so an absent `data` throws inside the
  // effect and takes the profile subtree down. Same shape as the documented
  // maternity defect. Reported, not fixed: substituting `[]` here would render
  // the panel un-highlighted instead of failing, which is a behaviour change.
  useEffect(() => {
    setHighlightID(getHighlightedObservationIds(props.data));
  }, [props?.data]);

  const authInfo = useAuthContext();

  const getDisplay = (code: string) => {
    const arr = code.toLowerCase().split('-');
    for (let i = 0; i < arr.length; i++) {
      arr[i] = arr[i]!.charAt(0).toUpperCase() + arr[i]!.slice(1);
    }
    const str = arr.join(' ');
    return str;
  };

  const resourcesSelectionOption = [
    {
      id: 'PW-RESOURCE-SENT_BY_EMAIL_TEXT',
      text: 'Resources sent by email/text',
    },
    {
      id: 'PW-RESOURCE-PRINTED_RESOURCES_ENGLISH',
      text: 'Printed Resources (English)',
    },
    {
      id: 'PW-RESOURCE-PRINTED_RESOURCES_SPANISH',
      text: 'Printed Resources (Spanish)',
    },
    {
      id: 'PW-RESOURCE-NO_RESOURCES_INFORMATION_REQUESTED',
      text: 'No resources information requested',
    },
  ];

  const communicationSelectionOption = [
    {
      id: 'PW-DO_NOT_CONTACT',
      text: 'Do Not Contact',
    },
    {
      id: 'PW-PHONE_ONLY',
      text: 'Phone Only',
    },
    {
      id: 'PW-EMAIL',
      text: 'Email',
    },
    {
      id: 'PW-TEXT',
      text: 'Text',
    },
    {
      id: 'PW-PHONE',
      text: 'Phone',
    },
  ];

  const EligibilityStatusSelectionOption = [
    {
      id: 'PW-FFS_ELIGIBLE',
      text: 'FFS Eligible',
    },
    {
      id: 'PW-FFS_NOT_ELIGIBLE',
      text: 'FFS Not Eligible',
    },
    {
      id: 'PW-ACTIVE_IN_CURRENT_PROGRAM',
      text: 'Active in current program',
    },
    {
      id: 'PW-CC_ON_FILE',
      text: 'CC on file',
    },
  ];

  /**
   * The three `{ id, text }` arrays above are not {@link Option}s — they carry
   * `id`/`text`, whereas `Option` is the `{ key, text, value }` triple semantic-ui
   * consumes. This maps the former onto the latter.
   */
  type SelectionOption = { id: string; text: string };

  const DROPDOWN_OPTIONS = (option: SelectionOption[]): Option[] => {
    return option.map((option: SelectionOption) => ({
      key: option.id,
      text: option.text,
      value: option.text,
    }));
  };

  /**
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. Five of the six
   * `observation.resource.category[0].coding[0].display` reads below are
   * unguarded, and `category` is optional in FHIR R4 — the server omits it on an
   * Observation that was entered manually rather than generated by an
   * intervention.
   *
   * The sixth read, in the "From …" line, IS guarded
   * (`observation.resource.category ? … : 'Manually added'`), and the line above
   * it uses the same test to choose between "From <source>" and "Manually
   * added". So this component already knows category-less observations exist and
   * already renders them; it is the five status labels
   * (INTERVENTION-SELECTED, SETUP-SUCCESS, SETUP-FAILED, SETUP-CANCELLED, and
   * the second SETUP-FAILED) that assume otherwise and throw during render,
   * taking the member's whole profile tab down rather than one row.
   *
   * Those five codes are written today only by the intervention pipeline, which
   * always sets a category, which is why the crash is latent. The two `[0]`
   * subscripts on each chain are a second, separate absence with the same
   * consequence — a category or coding present but empty.
   */
  const observationsList = (observation: ProfileObservationEntry, i: number, _counter: number) => {
    const parsedObservation = parseFHIRPreference(observation.resource);
    if (educationFilter) {
      if (
        !parsedObservation.accessMethod ||
        !educationFilter.includes(parsedObservation.accessMethod)
      ) {
        return;
      }
    }
    if (!props.sidebar) {
      return (
        <Popup
          key={i}
          flowing
          trigger={
            ['PAYMENT-DETAILS', 'PAYMENT-FAILED'].includes(parsedObservation.code) ? (
              parsedObservation.code === 'PAYMENT-FAILED' ? (
                <Label
                  basic={!highlightID?.includes(parsedObservation?.id)}
                  style={{ margin: '0.15em' }}
                  color={highlightID?.includes(parsedObservation?.id) ? 'teal' : 'grey'}
                >
                  ${parsedObservation.paymentAmount} {parsedObservation.productCode} payment failed
                </Label>
              ) : (
                <Label
                  basic={!highlightID?.includes(parsedObservation?.id)}
                  style={{ margin: '0.15em' }}
                  color={highlightID?.includes(parsedObservation?.id) ? 'teal' : 'grey'}
                >
                  ${parsedObservation.paymentAmount} {parsedObservation.productCode} payment
                  received
                </Label>
              )
            ) : parsedObservation.code === 'INTERVENTION-SELECTED' ? (
              <Label
                basic={!highlightID?.includes(parsedObservation?.id)}
                style={{ margin: '0.15em' }}
                color={highlightID?.includes(parsedObservation?.id) ? 'teal' : 'grey'}
              >
                Suggested {observation.resource.category![0]!.coding![0]!.display} intervention with
                score: {parsedObservation.valueString}{' '}
              </Label>
            ) : parsedObservation.code === 'SETUP-SUCCESS' ? (
              <Label
                basic={!highlightID?.includes(parsedObservation?.id)}
                style={{ margin: '0.15em' }}
                color={highlightID?.includes(parsedObservation?.id) ? 'teal' : 'grey'}
              >
                {observation.resource.category![0]!.coding![0]!.display} setup successful
              </Label>
            ) : parsedObservation.code === 'SETUP-FAILED' ? (
              <Label
                basic={!highlightID?.includes(parsedObservation?.id)}
                style={{ margin: '0.15em' }}
                color={highlightID?.includes(parsedObservation?.id) ? 'teal' : 'grey'}
              >
                {observation.resource.category![0]!.coding![0]!.display} setup failed due to "
                {parsedObservation.failureMessage}"
              </Label>
            ) : parsedObservation.code === 'SETUP-CANCELLED' ? (
              <Label
                basic={!highlightID?.includes(parsedObservation?.id)}
                style={{ margin: '0.15em' }}
                color={highlightID?.includes(parsedObservation?.id) ? 'teal' : 'grey'}
              >
                {observation.resource.category![0]!.coding![0]!.display} setup cancelled
              </Label>
            ) : (
              <Label
                basic={
                  !(highlightID?.includes(parsedObservation?.id) || props.code === 'FOOD-AVOIDANCE')
                }
                style={{ margin: '0.15em' }}
                color={
                  highlightID?.includes(parsedObservation?.id) || props.code === 'FOOD-AVOIDANCE'
                    ? 'teal'
                    : 'grey'
                } //always highlight all food avoidance items
              >
                {parsedObservation.valueString}{' '}
                {props.code !== 'REFERRAL-TYPE' &&
                  props.code !== 'PAYMENT-DETAILS' &&
                  props.code !== 'PAYMENT-FAILED' &&
                  props.code !== 'QUALIFICATION-STATUS' &&
                  props.code !== 'EDUCATIONAL-MATERIAL-PREFERENCES,access-content' && (
                    <Icon name="delete" onClick={() => handleDelete(i)} />
                  )}
              </Label>
            )
          }
          content={
            !['PAYMENT-DETAILS', 'PAYMENT-FAILED', 'SETUP-FAILED'].includes(
              parsedObservation.code,
            ) ? (
              parsedObservation.code === 'INTERVENTION-SELECTED' ? (
                <span key={i}>
                  <Icon name="clock" />
                  {parsedObservation.accessMethod &&
                    'Access Method: ' + parsedObservation.accessMethod + '. '}
                  Last Updated By: {parsedObservation.author}.{' '}
                  {moment(parsedObservation.lastUpdated).format('MMMM Do YYYY, h:mm:ss a')}.{' '}
                  {observation.resource.category
                    ? `From ${parsedObservation.author as unknown as string}`
                    : 'Manually added'}
                  .
                </span>
              ) : (
                <span key={i}>
                  <Icon name="clock" />
                  {parsedObservation.accessMethod &&
                    'Access Method: ' + parsedObservation.accessMethod + '. '}
                  Last Updated By: {parsedObservation.author}.{' '}
                  {moment(parsedObservation.lastUpdated).format('MMMM Do YYYY, h:mm:ss a')}.{' '}
                  {observation.resource.category
                    ? `From ${observation.resource.category[0]!.coding![0]!.display}`
                    : 'Manually added'}
                  .
                </span>
              )
            ) : parsedObservation.code === 'PAYMENT-FAILED' ? (
              <span key={i}>
                <Icon name="clock" />${parsedObservation.paymentAmount} payment for{' '}
                {parsedObservation.productCode} failed due to "{parsedObservation.failureMessage}".
                Last Updated By: {parsedObservation.author}.{' '}
                {moment(parsedObservation.lastUpdated).format('MMMM Do YYYY, h:mm:ss a')}.{' '}
              </span>
            ) : parsedObservation.code === 'SETUP-FAILED' ? (
              <span key={i}>
                <Icon name="clock" />
                {observation.resource.category![0]!.coding![0]!.display} setup failed due to "
                {parsedObservation.failureMessage}". Last Updated By: {parsedObservation.author}.{' '}
                {moment(parsedObservation.lastUpdated).format('MMMM Do YYYY, h:mm:ss a')}.{' '}
              </span>
            ) : (
              <span key={i}>
                <Icon name="clock" />${parsedObservation.paymentAmount} payment received for{' '}
                {parsedObservation.productCode}. Last Updated By: {parsedObservation.author}.{' '}
                {moment(parsedObservation.lastUpdated).format('MMMM Do YYYY, h:mm:ss a')}.{' '}
              </span>
            )
          }
          on="hover"
        />
      );
    } else {
      return (
        <Label
          key={i}
          basic
          style={{ margin: '0.15em' }}
          color={highlightID?.includes(parsedObservation?.id) ? 'teal' : 'grey'}
        >
          {parsedObservation.valueString}
          {parsedObservation.code === 'REFERRAL-TYPE' && parsedObservation.referralStatus
            ? ': ' + parsedObservation.referralStatus
            : ''}
        </Label>
      );
    }
  };

  const handleDelete = (i: number) => {
    setLoading('Deleting Profile Label...');
    setpopOpen(false);
    softDeleteObservation({
      // Reached only from a row's delete control, so `data` was non-empty when
      // the row rendered and `i` is that row's index within it.
      observation: props.data![i]!.resource,
      token: authInfo.token,
      patientId: props.patientId,
      practitionerId: authInfo.practitionerId,
      practitionerName: authInfo.practitionerName,
      org: props.org,
    }).then(
      () => {
        setLoading('Deletion Success. Updating Profile UI...');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
        props.mutate().then(() => {
          setLoading('');
        });
      },
      (error: RejectionWithMessage) => {
        setLoading(`Error - Unable to delete profile label: ${error.message}`);
      },
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let value;
    if (!resourceOption && !communicationOption && !eligibilityStatusOption) {
      value = (e.target as SubmittedForm).elements.newobs.value;
      // Spelling suggestions need an OpenAI key, and this is a browser bundle: every value the
      // build substitutes in is readable by anyone who loads the page, so no credential can be
      // held here. Without a suggestion to offer, submit what was typed — the same path taken
      // when the suggestion request returns nothing.
      //
      // Deferred to a microtask so the ORDER matches upstream. There, `submitSuggestion` runs
      // inside `OpenAIPoster(...).then(...)`, so the synchronous `setShowSuggestions(true)` at the
      // tail of this handler runs FIRST and `submitSuggestion`'s own `setpopOpen(false)` and
      // `setSuggestions([])` follow. Calling it synchronously here reversed that, leaving
      // `showSuggestions === true` with `suggestions === []` — which renders an empty, still
      // clickable label while hiding the entry form. `Promise.resolve().then` reproduces
      // upstream's microtask timing on the path a missing credential already takes.
      /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- the value is a `fhir-sdoh` export whose module ships no usable types */
      void Promise.resolve().then(() => submitSuggestion(value));
    } else {
      submitSuggestion(resourceOption || communicationOption || eligibilityStatusOption);
    }

    setShowSuggestions(true);
  };

  /**
   * Posts one profile label.
   *
   * `suggestion` arrives as the typed input's DOM `value` (a `string`), a
   * `<Select>` state (semantic-ui's `DropdownProps['value']`), or a
   * `suggestions[i]` entry (a `string`). Only the string forms reach `.trim()`
   * below without throwing; every option in this file declares a string
   * `value`, so the numeric/array arm is unreachable in practice.
   */
  const submitSuggestion = (suggestion: DropdownProps['value']) => {
    setpopOpen(false);
    setSuggestions([]);
    if (!suggestion || suggestion === '') {
      return false;
    }
    setLoading('Submitting Profile Label...');
    const body: fhir4.Observation = ObservationBuilder.buildPreferenceObservation(
      props.patientId,
      new Date().toJSON(),
      { code: props.code, display: getDisplay(props.code) },
      (suggestion as string).trim(),
      authInfo.practitionerName,
      props.org,
    );
    FHIRPoster(`/Observation/`, authInfo.token, body).then(
      () => {
        setLoading('Submission Success. Updating Profile UI...');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
        props.mutate().then(() => {
          setLoading('');
          setResourceOption('');
          setCommunicationOption('');
          setEligibilityStatusOption('');
        });
      },
      (error: RejectionWithMessage) => {
        setLoading(`Error - Unable to submit profile label: ${error.message}`);
      },
    );
  };

  function showPopupSuggestion() {
    if (!showSuggestions) {
      return (
        <Popup
          trigger={
            <div>
              {showSuggestionsContent()}
              {!showSuggestions && (
                <Form onSubmit={handleSubmit}>
                  <Form.Group>
                    <Form.Input placeholder="Enter New Value" name="newobs" />
                    <Form.Button primary content="Add" />
                  </Form.Group>
                </Form>
              )}
            </div>
          }
          content={<Label as="a">Please add multiple preferences one at a time</Label>}
          on="hover"
          position="bottom right"
        />
      );
    } else if (suggestions.length > 1) {
      return <div>{showSuggestionsContent()}</div>;
    }
  }

  function showSuggestionsContent() {
    return (
      showSuggestions && (
        <div>
          <div>
            <Header as="h5">Entered Value: </Header>
            <Label color={'black'} as="a" onClick={() => submitSuggestion(suggestions[0])}>
              {suggestions[0]}
            </Label>
          </div>
          <hr></hr>
          <div>
            <Header as="h5">Suggested Value: </Header>
            {suggestions.map(
              (suggestion: string, index: number) =>
                index !== 0 && (
                  <Label color={'teal'} as="a" onClick={() => submitSuggestion(suggestion)}>
                    {suggestion}
                  </Label>
                ),
            )}
          </div>
        </div>
      )
    );
  }
  return (
    <>
      {loading.length > 0 && <Loader active inline="centered" content={loading} />}
      {props.data && (
        <>
          {props.code === 'EDUCATIONAL-MATERIAL-PREFERENCES,access-content' && (
            <Button.Group size="mini">
              <Button
                content={'All'}
                color={educationFilter === null ? 'blue' : 'grey'}
                onClick={() => {
                  setEducationFilter(null);
                }}
              />
              <Button
                content={'Lesson ONLY'}
                color={educationFilter === 'member-app-task' ? 'blue' : 'grey'}
                onClick={() => {
                  /* eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type */
                  educationFilter === 'member-app-task'
                    ? setEducationFilter(null)
                    : setEducationFilter('member-app-task');
                }}
              />
              <Button
                content={'Library ONLY'}
                color={educationFilter === 'member-app-resource-library' ? 'blue' : 'grey'}
                onClick={() => {
                  /* eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type */
                  educationFilter === 'member-app-resource-library'
                    ? setEducationFilter(null)
                    : setEducationFilter('member-app-resource-library');
                }}
              />
            </Button.Group>
          )}
          {props.data.map((observation: ProfileObservationEntry, i: number) =>
            observationsList(observation, i, props.data!.length),
          )}

          {!props.sidebar &&
            props.code !== 'REFERRAL-TYPE' &&
            props.code !== 'QUALIFICATION-STATUS' &&
            props.code !== 'RESOURCES' &&
            props.code !== 'COMMUNICATION-PREFERENCES' &&
            props.code !== 'EDUCATIONAL-MATERIAL-PREFERENCES,access-content' &&
            props.code !== 'ELIGIBILITY-STATUS' && (
              <Popup
                trigger={<Label as="a">+</Label>}
                content={() => showPopupSuggestion()}
                on="click"
                position="bottom right"
                open={popOpen}
                onOpen={handlePopOpen}
                onClose={handleClose}
              />
            )}
          {!props.sidebar && props.code === 'ELIGIBILITY-STATUS' && (
            <Popup
              trigger={<Label as="a">+</Label>}
              content={
                <Form onSubmit={(e, { value: _value }) => handleSubmit(e)}>
                  <Form.Group>
                    <Form.Field
                      name="option"
                      label="ELIGIBILITY STATUS OPTION:"
                      selectOnBlur={false}
                      control={Select}
                      search
                      key="option"
                      placeholder="Select an option"
                      value={eligibilityStatusOption}
                      options={DROPDOWN_OPTIONS(EligibilityStatusSelectionOption)}
                      onChange={(_e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) =>
                        setEligibilityStatusOption(value)
                      }
                    />
                    <Form.Button primary content="Add" />
                  </Form.Group>
                </Form>
              }
              on="click"
              position="bottom right"
              open={popOpen}
              onOpen={handlePopOpen}
              onClose={handleClose}
            />
          )}
          {!props.sidebar && props.code === 'COMMUNICATION-PREFERENCES' && (
            <Popup
              trigger={<Label as="a">+</Label>}
              content={
                <Form onSubmit={(e, { value: _value }) => handleSubmit(e)}>
                  <Form.Group style={{ alignItems: 'flex-end' }}>
                    <Form.Field
                      name="option"
                      label="COMMUNICATION OPTION:"
                      selectOnBlur={false}
                      control={Select}
                      search
                      key="option"
                      placeholder="Select an option"
                      value={communicationOption}
                      options={DROPDOWN_OPTIONS(communicationSelectionOption)}
                      onChange={(_e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) =>
                        setCommunicationOption(value)
                      }
                    />
                    <Form.Button primary content="Add" />
                  </Form.Group>
                </Form>
              }
              on="click"
              position="bottom right"
              open={popOpen}
              onOpen={handlePopOpen}
              onClose={handleClose}
            />
          )}
          {!props.sidebar && props.code === 'RESOURCES' && (
            <Popup
              trigger={<Label as="a">+</Label>}
              content={
                <Form onSubmit={(e, { value: _value }) => handleSubmit(e)}>
                  <Form.Group>
                    <Form.Field
                      name="option"
                      label="RESOURCE OPTION:"
                      selectOnBlur={false}
                      control={Select}
                      search
                      key="option"
                      placeholder="Select an option"
                      value={resourceOption}
                      options={DROPDOWN_OPTIONS(resourcesSelectionOption)}
                      onChange={(_e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) =>
                        setResourceOption(value)
                      }
                    />
                    <Form.Button primary content="Add" />
                  </Form.Group>
                </Form>
              }
              on="click"
              position="bottom right"
              open={popOpen}
              onOpen={handlePopOpen}
              onClose={handleClose}
            />
          )}
        </>
      )}
    </>
  );
}
